import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'

import { LocalStorageSplitButton } from '@components/LocalStorageSplitButton'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import type { GroupedFilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { queryKey } from '../hooks/useGeblokkeerdeWekenSearch'
import type { GeblokkeerdeWekenSearchFilters } from '../types/GeblokkeerdeWekenSearch'

import { GeblokkeerdeWeekDialog } from './dialogs/GeblokkeerdeWeekDialog'

interface Props {
  onChange: (filters: GeblokkeerdeWekenSearchFilters) => void
  filters: GeblokkeerdeWekenSearchFilters
  initialFilters: GeblokkeerdeWekenSearchFilters
  onReset: () => void
  filterOptions: {
    gebieden: Gebied[]
    hoofdleiding: {
      verantwoordelijken: GroupedFilterOption[]
    }
  }
}

export const GeblokkeerdeWekenFilters = ({
  onChange,
  filters,
  initialFilters,
  onReset,
  filterOptions: {
    gebieden,
    hoofdleiding: { verantwoordelijken },
  },
}: Props) => {
  const [open, setOpen] = useState(false)

  const { canCreateGeblokkeerdeWeek } = useAuthenticatedUser()
  const [localFilters, setLocalFilters] =
    useState<GeblokkeerdeWekenSearchFilters>(filters)

  const handleFilterChange = (filter: Partial<typeof filters>) => {
    const newFilters = { ...localFilters, ...filter }
    setLocalFilters(newFilters)
    onChange(newFilters)
  }

  const handleReset = () => {
    setLocalFilters(initialFilters)
    onReset()
  }

  return (
    <>
      <Grid spacing={2} container>
        <FilterGridItem>
          <FilterSelectGebied
            options={gebieden}
            value={localFilters.gebied}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>

        <FilterGridItem>
          <FilterSelectObject
            options={verantwoordelijken}
            label="Projectverantwoordelijke"
            value={localFilters.organisatie}
            filterKey="organisatie"
            onFilterChange={handleFilterChange}
            variant="outlined"
          />
        </FilterGridItem>

        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ flexGrow: 1 }}></Box>

            <Box sx={{ gap: 2, display: 'flex' }}>
              {canCreateGeblokkeerdeWeek && (
                <Button
                  variant="text"
                  endIcon={<AddIcon />}
                  onClick={() => setOpen(true)}
                >
                  Week toevoegen
                </Button>
              )}

              <LocalStorageSplitButton
                name={queryKey}
                onLoad={handleFilterChange}
                onReset={handleReset}
                storable={filters}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      {open && (
        <GeblokkeerdeWeekDialog
          onClose={() => setOpen(false)}
          gebieden={gebieden}
        />
      )}
    </>
  )
}
